import { Helmet } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';
import GlobalStyle from './GlobalStyle';
import { MetronomeProvider } from '@ally/metronome-ui';
import { ScrollToTop } from './components/atoms';
import {
  useAppCache,
  useConfig,
  useCustomersService,
  useEventListener,
  useScript,
  useSession,
  useWealthController,
  useLogRocket,
} from './hooks';
import { useEffect } from 'react';
import { Routes } from './components/organisms';
import { ViewportProvider } from './contexts';
import allytm from '@allysf/allytm';
import { muiAllyTheme } from './utils';
import { readCookie } from './utils/CookieUtil';

function App() {
  const { hydrateClient } = useWealthController();
  const { getACMStatus } = useCustomersService();
  const { isLoggedIn, session, logout, getLocalStorage } = useSession(true);
  const { orionConfig, allyTmUrl, environment } = useConfig();
  const { loadScript } = useScript(
    `${orionConfig.baseUrl}/widgets/commons.bundle.js`,
    'orion-common'
  );
  const allyTmScript = useScript(allyTmUrl, 'ally-tm');
  const { appCache, getRelationship } = useAppCache();
  const { initializeLogRocket, LogRocket } = useLogRocket();
  const { addListener } = useEventListener();

  useEffect(() => {
    if (environment === 'qa' || environment === 'prod') {
      initializeLogRocket();
    }
    allyTmScript.loadScript();
    allytm();
    loadScript();
    // Trigger to log a user off if they log off from another tab
    const logoutListener = (): void => {
      if (getLocalStorage('logout')) {
        logout();
      }
    };
    addListener('storage', logoutListener);
  }, []);

  useEffect(() => {
    if (environment === 'qa' || environment === 'prod') {
      LogRocket.identify(session.username || '', {
        guid: session.guid || '',
        investId: session.sviuserid || '',
        sessionId: readCookie('TLTSID'),
        application: 'Personal Advice',
      });
      LogRocket.getSessionURL((sessionURL: string) => {
        if (window.allytm && window.allytm.ddo) {
          window.allytm.ddo.set('LogRocket.sessionURL', sessionURL);
        }
      });
    }

    if (
      isLoggedIn &&
      !appCache.relationshipList.hasLoaded &&
      !appCache.relationshipList.isLoading
    ) {
      const checkACMStatus = async () => {
        const relationshipList = await getACMStatus();
        if (getRelationship('WEALTH', relationshipList) && session.username) {
          hydrateClient();
        }
      };
      checkACMStatus();
    }
  }, [isLoggedIn]);

  return (
    <>
      <Helmet>
        <meta
          httpEquiv='Content-Security-Policy'
          content={`
                      default-src 'self' 'unsafe-inline' *.gstatic.com;
                      style-src 'self' 'unsafe-inline' *.ally.com;
                      script-src 'self' 'unsafe-inline' 'unsafe-eval' blob: *.ally.com *.plaid.com *.adobedtm.com *.lr-hv-in.com *.advizr.com *.googletagmanager.com *.doubleclick.net *.google-analytics.com;
                      img-src 'self' data: *.ally.com *.demdex.net *.everesttech.net *.google.com;
                      media-src 'self' *.ally.com;
                      worker-src 'self' data: blob:;
                      connect-src 'self' wss ws *.ally.com *.demdex.net localhost:* *.lr-hv-in.com *.advizr.com *.google-analytics.com *.google.com *.techlab-cdn.com *.akstat.io;
                      frame-src 'self' *.ally.com *.demdex.net *.plaid.com *.googletagmanager.com;
                `}
        ></meta>
      </Helmet>
      <GlobalStyle />
      <ViewportProvider>
        <MetronomeProvider theme={muiAllyTheme}>
          <Router>
            <ScrollToTop />
            <Routes />
          </Router>
        </MetronomeProvider>
      </ViewportProvider>
    </>
  );
}

export default App;
